import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormControl,
    Input,
    InputLabel,
    FormHelperText,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Chip,
    Popper,
    Box,
    Typography,
    Button,
} from '@mui/material';
import { ROLE } from '../../../../../constants';

export const UserCashRegister = ({
    cashRegistersSet,
    cashRegistersList,
    setCashRegister,
    disabled,
    pageTitle,
    userId,
    handleCashRegisterAccess,
    userRoles,
}) => {
    const { t } = useTranslation();
    const [selectedCashId, setSelectedCashId] = useState([]);
    const [updatedCashRegisters, setUpdatedCashRegisters] = useState([]);
    const [checkboxCurrentEvent, setCheckboxCurrentEvent] = useState({});
    const [activeModalId, setActiveModalId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const idPopup = open ? 'removePopup' : undefined;
    const isEditUserPage = pageTitle === 'edit_user';
    const isAddUserPage = pageTitle === 'add_user';
    const hasCashierRole = userRoles.some(
        roleObj => roleObj.role === ROLE.cashier && !!roleObj.id,
    );

    useEffect(() => {
        const filteredCashIds = cashRegistersSet
            .map(register => {
                const { cashRegisterId, active = true } = register;
                return active ? cashRegisterId : undefined;
            })
            .filter(Boolean);

        setSelectedCashId(filteredCashIds);

        setUpdatedCashRegisters(
            cashRegistersSet.map(register => {
                const { cashRegisterId, active = true } = register || [];

                return {
                    cashRegisterId,
                    active: active,
                };
            }),
        );
    }, [cashRegistersSet]);

    const handleSelectCashRegister = event => {
        const { value: selectedIds } = event.target;
        setSelectedCashId(selectedIds);

        const updatedCashRegisterList = cashRegistersList.map(register => {
            const { id } = register;
            return {
                cashRegisterId: id,
                active: selectedIds.includes(id),
            };
        });
        setCashRegister(updatedCashRegisterList);
    };

    const handleCheckboxClick = event => {
        const { target, currentTarget } = event;
        const { id } = target;
        const targetId = parseFloat(id);
        setCheckboxCurrentEvent(event);
        setActiveModalId(targetId);
        setAnchorEl(anchorEl ? null : currentTarget);
    };

    const handleConfirm = (confirm, event) => {
        if (confirm) {
            const { target } = event;
            const { id, checked } = target;
            const targetId = parseFloat(id);

            setUpdatedCashRegisters(prevState =>
                prevState.map(register =>
                    register.cashRegisterId === targetId
                        ? { ...register, active: checked }
                        : register,
                ),
            );
            setSelectedCashId(prevState =>
                prevState.includes(targetId)
                    ? prevState.filter(cashId => cashId !== targetId)
                    : [...prevState, targetId],
            );

            const mutationObj = {
                cashRegisterId: targetId,
                create: !checked,
                userId,
            };
            handleCashRegisterAccess(mutationObj);
        }
        setActiveModalId(null);
        setAnchorEl(null);
        setCheckboxCurrentEvent({});
    };

    if (isEditUserPage && hasCashierRole) {
        return (
            <div>
                <InputLabel fullwidth='true' sx={{ m: 1 }} variant='standard'>
                    {t('select_cash_register')}
                </InputLabel>
                {cashRegistersList.map(register => {
                    const { id, number } = register;
                    const selectedRegister = updatedCashRegisters.find(
                        updatedRegister =>
                            updatedRegister.cashRegisterId === id,
                    );
                    const { active = false } = selectedRegister || [];

                    return (
                        <InputLabel
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                            key={id}
                            value={id}
                        >
                            <Checkbox
                                checked={active}
                                id={id.toString()}
                                onChange={handleCheckboxClick}
                            />
                            <ListItemText
                                primary={number}
                                sx={{ margin: '0 0' }}
                            />

                            {activeModalId === id && (
                                <Popper
                                    id={idPopup}
                                    open={open}
                                    anchorEl={anchorEl}
                                >
                                    <Box
                                        component='div'
                                        sx={{
                                            display: 'block',
                                            backgroundColor: 'white',
                                            boxShadow:
                                                '0px 4px 8px rgb(0 0 0 / 0.1)',
                                            borderRadius: '8px',
                                            padding: '20px',
                                            border: '1px solid rgb(0 0 0 / 0.2)',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant='body1'
                                            component='div'
                                            sx={{ marginBottom: 1 }}
                                        >
                                            {t('are_you_sure')}
                                        </Typography>
                                        <Button
                                            variant='outlined'
                                            sx={{ marginRight: 1 }}
                                            onClick={() =>
                                                handleConfirm(
                                                    true,
                                                    checkboxCurrentEvent,
                                                )
                                            }
                                        >
                                            {t('yes')}
                                        </Button>
                                        <Button
                                            variant='outlined'
                                            onClick={() =>
                                                handleConfirm(
                                                    false,
                                                    checkboxCurrentEvent,
                                                )
                                            }
                                        >
                                            {t('no')}
                                        </Button>
                                    </Box>
                                </Popper>
                            )}
                        </InputLabel>
                    );
                })}
            </div>
        );
    }

    if (isAddUserPage) {
        return (
            <FormControl fullwidth='true' sx={{ m: 1 }} variant='standard'>
                <InputLabel htmlFor='component-cash-register'>
                    {t('cash_register')}
                </InputLabel>
                <Select
                    id='component-cash-register'
                    labelId='demo-simple-select-helper-label'
                    label={t('cash-register')}
                    value={selectedCashId}
                    onChange={handleSelectCashRegister}
                    input={<Input label='Chip' />}
                    name='cash-registers'
                    disabled={disabled}
                    multiple
                    renderValue={selected => (
                        <>
                            {selected.map(item => {
                                if (
                                    cashRegistersList?.find(e => e.id === item)
                                ) {
                                    return (
                                        <Chip
                                            key={item}
                                            label={
                                                cashRegistersList?.find(
                                                    e => e.id === item,
                                                )?.number || ''
                                            }
                                            sx={{ height: '23px' }}
                                        />
                                    );
                                }
                            })}
                        </>
                    )}
                >
                    {cashRegistersList.map(register => {
                        const { id, number } = register;
                        const selectedRegister = updatedCashRegisters.find(
                            updatedRegister =>
                                updatedRegister.cashRegisterId === id,
                        );
                        const { active = false } = selectedRegister || [];

                        return (
                            <MenuItem key={id} value={id}>
                                <Checkbox
                                    checked={active}
                                    sx={{ padding: '0 2px 0' }}
                                />
                                <ListItemText
                                    primary={number}
                                    sx={{ margin: '0 0' }}
                                />
                            </MenuItem>
                        );
                    })}
                </Select>
                <FormHelperText id='standard-helper-text'>
                    {t('select_cash_register')}
                </FormHelperText>
            </FormControl>
        );
    }
    return null;
};
